import { Badge } from '@dixa/lib-shared-habla';
import { convertChannelCasing } from 'lib-shared-api-utils';
import type { Message } from 'lib-shared-legacy-types';
import type {
  ConversationPstnPhoneMessageAddedEvent,
  ConversationPstnVoicemailMessageAddedEvent,
  ConversationTextualMediaMessageAddedEvent,
} from 'lib-shared-types-api';
import { ComponentProps } from 'react';

import { getMessageMarkup } from 'app/utils/messages';
import { HTMLShadowDomRenderer } from 'components/HTMLShadowDomRenderer';
import { ConversationEventGroupConfigBase } from 'components/conversation/conversationEventGroups/conversationEventGroups.types';

import { ConversationTextualMessage } from '../../textualMessage/conversationTextualMessage';
import { ConversationTextualMessagePropBase } from '../../textualMessage/conversationTextualMessage.types';

/** Represents the conversation transcript event group configuration. */
export type ConversationTranscriptEventGroupConfig =
  ConversationEventGroupConfigBase<
    'group:pstn_phone' | 'group:voicemail',
    Pick<
      ConversationTranscriptEventGroupProps,
      'hideAttachments' | 'hideGutterActions' | 'stickyHeader'
    >
  >;

type ConversationTranscriptEventGroupProps = ConversationTextualMessagePropBase<
  | ConversationPstnPhoneMessageAddedEvent
  | ConversationPstnVoicemailMessageAddedEvent,
  Pick<
    ComponentProps<typeof ConversationTextualMessage.Events>,
    'hideAttachments'
  >
>;

/**
 * Represents the component for displaying a conversation transcript event
 * group.
 */
export const ConversationTranscriptGroup = ({
  author,
  className,
  events,
  hideAttachments,
  hideGutterActions,
  stickyHeader,
}: ConversationTranscriptEventGroupProps): JSX.Element => {
  return (
    <ConversationTextualMessage
      className={className}
      author={author}
      hideGutterActions={hideGutterActions}
      stickyHeader={stickyHeader}
      variant={
        events[0].direction === 'outbound'
          ? 'textualOutbound'
          : 'textualInbound'
      }
      authorEndSlot={
        <Badge size="small" variant="info">
          Transcribed
        </Badge>
      }
    >
      <ConversationTextualMessage.Events
        events={
          events as unknown as ConversationTextualMediaMessageAddedEvent[]
        }
        hideAttachments={hideAttachments}
        renderEventText={(event) => {
          const { channel, ...rest } = event;
          const truncatedChannel = convertChannelCasing(channel, 'truncate');

          return (
            event.text && (
              <HTMLShadowDomRenderer
                html={getMessageMarkup({
                  channel: truncatedChannel,
                  ...rest,
                } as Message)}
              />
            )
          );
        }}
      />
    </ConversationTextualMessage>
  );
};
